import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Plus, X } from "lucide-react";
import { api } from "@/lib/api";

const STATUS_STYLE = {
  pending: "border-amber-400 text-amber-600",
  approved: "border-emerald-500/50 text-emerald-600",
  declined: "border-slate-300 text-slate-500",
  expired: "border-slate-300 text-slate-400",
  withdrawn: "border-slate-300 text-slate-400",
  superseded: "border-slate-300 text-slate-400",
};

export default function OpsAuthorizations() {
  const [issues, setIssues] = useState([]);
  const [auths, setAuths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null); // issue being requested against

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: iss }, { data: a }] = await Promise.all([
        api.get("/ops/issues", { params: { status: "open" } }),
        api.get("/ops/authorizations"),
      ]);
      setIssues(iss);
      setAuths(a);
    } catch (e) {
      // ops gate / permissions — stay quiet in dashboard
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const authByIssue = (id) => auths.filter((x) => x.issue_id === id);

  return (
    <div className="mt-8" data-testid="ops-authorizations">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck className="h-5 w-5 text-gold-500" />
        <h2 className="font-serif text-lg text-navy-900 dark:text-white">Owner authorizations</h2>
      </div>

      {loading ? (
        <div className="flex justify-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : issues.length === 0 ? (
        <div className="text-center py-10 bg-white dark:bg-navy-800 rounded-2xl border border-dashed border-slate-300 dark:border-white/10 text-slate-500" data-testid="ops-auth-no-issues">
          No open issues requiring owner authorization.
        </div>
      ) : (
        <div className="space-y-3">
          {issues.map((iss) => (
            <div key={iss.id} className="bg-white dark:bg-navy-800 border border-gold-500/15 rounded-2xl p-5" data-testid={`ops-auth-issue-${iss.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className={`eyebrow px-2 py-0.5 rounded-full border text-[10px] ${iss.severity === "URGENT" ? "border-red-400 text-red-500" : "border-amber-400 text-amber-600"}`}>{iss.severity}</span>
                  <p className="font-medium text-navy-900 dark:text-white mt-2">{iss.summary}</p>
                  {iss.component_key && <p className="text-xs text-slate-500 mt-0.5">{iss.component_key}</p>}
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {authByIssue(iss.id).map((a) => (
                      <span key={a.id} className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_STYLE[a.status] || "border-slate-300 text-slate-500"}`} data-testid={`ops-auth-badge-${a.id}`}>
                        {a.status}
                      </span>
                    ))}
                  </div>
                </div>
                <button onClick={() => setTarget(iss)} className="inline-flex items-center gap-1.5 bg-navy-900 dark:bg-gold-500 text-white dark:text-navy-900 text-sm font-semibold px-4 py-2 rounded-full shrink-0" data-testid={`ops-auth-request-${iss.id}`}>
                  <Plus className="h-4 w-4" /> Request authorization
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {target && (
        <RequestModal issue={target} onClose={() => setTarget(null)} onDone={() => { setTarget(null); load(); }} />
      )}
    </div>
  );
}

const RequestModal = ({ issue, onClose, onDone }) => {
  const [f, setF] = useState({
    requested_action: "Coordinate a contractor to further observe/address the documented item",
    scope: "",
    cost_mode: "none",
    cost_amount: "",
    expires_in_days: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((o) => ({ ...o, [k]: e.target.value }));

  const submit = async () => {
    if (!f.scope.trim()) { toast.error("Scope is required."); return; }
    if (f.cost_mode !== "none" && (f.cost_amount === "" || Number(f.cost_amount) < 0)) {
      toast.error("Enter a valid cost amount."); return;
    }
    setBusy(true);
    try {
      await api.post("/ops/authorizations", {
        vessel_id: issue.vessel_id,
        issue_id: issue.id,
        action_type: "coordinate_contractor",
        requested_action: f.requested_action,
        scope: f.scope,
        inspection_id: issue.inspection_id || null,
        evidence_photo_ids: issue.evidence_photo_ids || [],
        cost_mode: f.cost_mode,
        cost_amount: f.cost_mode === "none" ? null : Number(f.cost_amount),
        expires_in_days: f.expires_in_days ? Number(f.expires_in_days) : null,
      });
      toast.success("Authorization request sent to owner.");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create request.");
    } finally { setBusy(false); }
  };

  const inp = "w-full bg-ivory dark:bg-navy-900 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-navy-900 dark:text-white";

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="ops-auth-modal">
      <div className="bg-white dark:bg-navy-800 rounded-2xl p-6 w-full max-w-lg">
        <div className="flex items-center justify-between mb-4">
          <p className="font-serif text-lg text-navy-900 dark:text-white">Request owner authorization</p>
          <button onClick={onClose} data-testid="ops-auth-modal-close" className="text-slate-400"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-xs text-slate-500 mb-4">For: <b>{issue.summary}</b></p>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-500">Requested action</label>
            <input className={inp} value={f.requested_action} onChange={set("requested_action")} data-testid="ops-auth-action" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Bounded scope (exactly what HMC may coordinate)</label>
            <textarea className={inp} rows={3} value={f.scope} onChange={set("scope")} placeholder="e.g. Coordinate marine electrician to inspect and, if needed, replace the battery charger only." data-testid="ops-auth-scope" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500">Cost</label>
              <select className={inp} value={f.cost_mode} onChange={set("cost_mode")} data-testid="ops-auth-cost-mode">
                <option value="none">No cost specified</option>
                <option value="estimate">Estimate</option>
                <option value="not_to_exceed">Approved maximum (not to exceed)</option>
              </select>
            </div>
            {f.cost_mode !== "none" && (
              <div>
                <label className="text-xs text-slate-500">Amount (USD)</label>
                <input type="number" min="0" className={inp} value={f.cost_amount} onChange={set("cost_amount")} data-testid="ops-auth-cost-amount" />
              </div>
            )}
          </div>
          <div>
            <label className="text-xs text-slate-500">Expires in (days, optional)</label>
            <input type="number" min="1" className={inp + " max-w-[160px]"} value={f.expires_in_days} onChange={set("expires_in_days")} data-testid="ops-auth-expires" />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full border border-slate-200 dark:border-white/10 text-sm text-slate-600 dark:text-slate-300">Cancel</button>
          <button onClick={submit} disabled={busy} className="inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-400 disabled:opacity-60 text-navy-900 text-sm font-semibold px-5 py-2 rounded-full" data-testid="ops-auth-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Send request
          </button>
        </div>
      </div>
    </div>
  );
};
